<template>
    <div style="height: 100%">
        <div id="main" v-show="!rotaAninhada">
            <container-loader v-if="isLoading && !$store.getters.isLoading" />
            <div class="page">
                <div class="vertical-flow">
                    <div class="listing-header">
                        <!-- TÍTULO E BREADCRUMB -->
                        <div class="title-breadcrumb">
                            <h1 class="title">
                                {{ $t('funil') }}
                            </h1>
                            <nav class="breadcrumb" aria-label="breadcrumbs">
                                <ul>
                                    <li>
                                        <a class="a-inativo">{{ $t('vendas') }}</a>
                                    </li>
                                    <li>
                                        <a class="a-inativo">{{ $t('crm') }}</a>
                                    </li>
                                    <li>
                                        <a aria-current="page">{{ $t('funil') }}</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>

                        <!-- BOTOES -->
                        <div class="header-buttons">
                            <button
                                v-if="permissao.create && !funisIntegracao"
                                class="save-btn"
                                type="button"
                                tag="button"
                                @click="
                                    $router.push({
                                        name: 'ven-funil-new',
                                    })
                                "
                            >
                                {{ $t('adicionar') }}
                            </button>
                        </div>
                    </div>

                    <!-- PESQUISA E FILTROS -->
                    <div class="columns is-multiline">
                        <div class="column is-three-quarters">
                            <search-bar
                                :onSearch="debouncedOnSearch"
                                tooltipLabel="msg-busca-codigo-descricao"
                            />
                        </div>

                        <!-- SITUACAO -->
                        <div class="situacao-select column is-one-quarter">
                            <label v-text="$t('situacao')" />
                            <v-select
                                ref="situacaoInput"
                                class="is-100"
                                label="label"
                                :options="situacoes"
                                @input="updateSituacao"
                                v-model="situacao"
                            >
                                <template slot="option" slot-scope="option">
                                    {{ $t(option.label) }}
                                </template>
                                <template slot="selected-option" slot-scope="option">
                                    {{ $t(option.label) }}
                                </template>
                                <template slot="no-options">{{ $t('sem-resultados') }}</template>
                            </v-select>
                        </div>
                    </div>

                    <!-- LISTAGEM -->
                    <div class="registry-wrapper">
                        <empty-table-message v-if="isEmpty" />
                        <b-table
                            v-else
                            class="data-table"
                            :data="allFunis"
                            :mobile-cards="false"
                            @sort="sortTable"
                            backend-sorting
                        >
                            <b-table-column
                                v-for="(f, i) in fields"
                                class="td-center"
                                :key="i"
                                :field="f.key"
                                :label="f.key != 'excluir' ? $t(f.key) : ''"
                                :width="f.width"
                                :sortable="f.sortable"
                                v-slot="props"
                            >
                                <div v-if="f.key == 'excluir'">
                                    <template v-if="permissao.update && !funisIntegracao">
                                        <b-button
                                            v-if="props.row.situacao"
                                            type="is-danger"
                                            icon-right="delete"
                                            @click="confirmExclusion(props.row)"
                                            v-tippy
                                            :title="$t('desativar')"
                                        />
                                        <b-button
                                            v-else
                                            type="is-success"
                                            icon-right="check"
                                            @click="confirmReactivation(props.row)"
                                            v-tippy
                                            :title="$t('reativar')"
                                        />
                                    </template>
                                </div>
                                <div v-if="f.key == 'codigo'">
                                    <router-link
                                        :to="{
                                            name: 'ven-funil-edit',
                                            params: { id: props.row.id },
                                        }"
                                    >
                                        <pre>{{ props.row[f.key] }}</pre>
                                    </router-link>
                                </div>
                                <div v-else-if="f.key == 'descricao'">
                                    <router-link
                                        :to="{
                                            name: 'ven-funil-edit',
                                            params: { id: props.row.id },
                                        }"
                                    >
                                        <pre>{{ props.row[f.key] }}</pre>
                                    </router-link>
                                </div>
                                <div v-else-if="f.key == 'situacao'">
                                    <b-tag
                                        class="table-tag table-tag-full-width"
                                        v-if="props.row[f.key] == 1"
                                        type="is-success"
                                        size="is-medium"
                                        >{{ $t('ativo') }}</b-tag
                                    >
                                    <b-tag
                                        class="table-tag table-tag-full-width"
                                        v-else
                                        type="is-danger"
                                        size="is-medium"
                                        >{{ $t('inativo') }}</b-tag
                                    >
                                </div>
                                <div v-else>
                                    {{ props.row[f.key] }}
                                </div>
                            </b-table-column>
                        </b-table>
                        <PaginationViewer
                            class="mb-0"
                            :resetPagination="pagination.resetPagination"
                            :totalItems="pagination.totalRows"
                            :perPageOptions="pagination.perPageOptions"
                            :optionStarted="pagination.optionStarted"
                            @change="updatePagination"
                        />
                    </div>
                </div>
            </div>
        </div>
        <router-view />
    </div>
</template>

<script>
import FunilService from '@/services/FunilService';
import ContainerLoader from '@/components/content/loader/ContainerLoader';

export default {
    beforeRouteUpdate(to, from, next) {
        if (['ven-funil-new', 'ven-funil-edit'].includes(to.name)) {
            this.rotaAninhada = true;
            next();
        } else {
            this.rotaAninhada = false;
            this.carregarDados();
            next();
        }
    },
    components: {
        ContainerLoader,
    },
    data() {
        return {
            funisIntegracao: this.$store.state.user.tabelasERPWebServer.includes('CRM_FUNIL'),
            allFunis: [],
            service: new FunilService(this.$http, this.$router),
            fields: [
                { key: 'codigo', width: '7.5%', sortable: true },
                { key: 'descricao', width: null, sortable: true },
                { key: 'situacao', width: 80, sortable: true, sortFn: null },
                { key: 'excluir', width: 70, sortable: false },
            ],
            situacoes: [
                { label: 'todos', id: 2 },
                { label: 'ativo', id: 1 },
                { label: 'inativo', id: 0 },
            ],
            situacao: { label: 'todos', id: 2 },
            deleteThis: null,
            isLoading: false,
            isEmpty: false,
            situacaoAcao: null,
            debouncedOnSearch: null,
            column: 'descricao',
            order: 'asc',
            permissao: {},
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
        };
    },
    created() {
        if (['ven-funil-new', 'ven-funil-edit'].includes(this.$route.name)) {
            this.rotaAninhada = true;
        } else {
            this.rotaAninhada = false;
        }

        // Seleciona o valor na posicao do array de opcoes de paginacao
        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];
        this.permissao = this.hasPermissionAccess('vendas', 'crm', 'ven-funil');

        !this.permissao.read &&
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'funis' },
            });

        this.debouncedOnSearch = this.debounce(this.onSearch, 500);
        this.carregarDados();
    },
    methods: {
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        onSearch(value) {
            this.queryFilter = this.removeAcentos(value);
            this.carregarDados();
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;
            this.isLoading = true;
            this.service
                .findAll(
                    this.column,
                    this.order,
                    currentPage - 1,
                    this.pagination.perPage,
                    this.situacao.id,
                    this.queryFilter
                )
                .then(this.mapItems)
                .then((value) => {
                    this.allFunis = value;
                    this.isEmpty = this.allFunis.length <= 0;
                })
                .catch((e) => {
                    console.error('Erro ao carregar funil.', e);
                    this.isEmpty = true;
                    this.$root.showMessage('error', 'msg-erro-dados-geral');
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        mapItems(body) {
            if (body) {
                if (body.content) {
                    this.pagination.totalRows = body.totalElements;
                    let funilList = body.content.map((funis) => {
                        return {
                            id: funis.idFunil,
                            codigo: funis.idFunilErp,
                            descricao: funis.descricao,
                            situacao: funis.situacao,
                        };
                    });
                    return funilList;
                }
            } else {
                return [];
            }
        },
        confirmExclusion(item) {
            this.deleteThis = item;
            this.situacaoAcao = 0;

            this.$root.confirm({
                message: this.$t('msg-confirmacao-desativacao-n', { n: item.descricao }),
                onConfirm: this.updateSituacaoRegistro,
                onCancel: (_) => {
                    this.deleteThis = null;
                },
            });
        },
        confirmReactivation(item) {
            this.deleteThis = item;
            this.situacaoAcao = 1;
            this.$root.confirm({
                message: this.$t('msg-confirmacao-reativacao-n', { n: item.descricao }),
                onConfirm: this.updateSituacaoRegistro,
                onCancel: (_) => {
                    this.deleteThis = null;
                },
            });
        },
        updateSituacaoRegistro() {
            this.isLoading = true;
            let funil = {
                idFunil: this.deleteThis.id,
                situacao: this.situacaoAcao,
            };
            this.service
                .updateSituacao(funil)
                .then((r) => {
                    if (r.status == 200) {
                        let tempSuccess =
                            r.content.situacao == 1
                                ? 'msg-sucesso-ao-reativar-funil'
                                : 'msg-sucesso-ao-desativar-funil';
                        this.$root.showMessage('success', tempSuccess);
                        this.carregarDados(this.pagination.currentPage);
                    } else if (r.status == 500) {
                        this.$root.showMessage('error', 'msg-problema-realizar-acao');
                    }
                })
                .catch((e) => {
                    console.error('Erro ao remover funil.', e);
                    this.$root.showMessage('error', 'msg-problema-realizar-acao');
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.delete = null;
                });
        },
        sortTable(field, order) {
            this.column = field;
            this.order = order;
            this.carregarDados();
        },
        updateSituacao(t) {
            if (t) {
                this.situacao = t;
            } else {
                this.situacao = this.situacoes[0];
            }
            this.carregarDados();
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: 100%;
}

.page {
    position: relative;
    padding-left: 0;

    .title-breadcrumb {
        padding-bottom: 10px;
    }
    .situacao-select div,
    .produtivo-select div {
        height: 40px;
    }
}
</style>
